import { TelemetryService, CustomKeyValues } from "../../ShellCommon/logger/telemetry-service";
import { GlobalTelemetryConstants } from "../../ShellCommon/logger/telemetry-constants";
import { LoggingService } from "../sdServices/loggingSvc";
import { UiControlConstants } from "../../ShellCommon/common/uicontrol-constants";

type PageActionTriggerOptions = {
  // Space separated list of jquery events to trigger an event.
  events: string;
  // comma separated array of custom angular events to trigger an event. Eg: angularEvents: ['event1', 'event2'].
  angularEvents: string[];
  // Space separated list of key names to trigger an event.
  keypresses: string;
  // This should be an expression resolving to an enumeration name from ServiceDesk.Services.PageActionBehavior enum.
  behavior: string;
  // The model value that is being watched for change for input change telemtry. Optional.
  model: any;
  // scenario under which the action should be logged
  scenario: string;
  // scenario step under the scenario.
  scenarioStep: string;
  // The page intent where the control is currently residing
  pageType: string;
  // Any additional info (key value pairs) to be part of page tags
  pageTags: CustomKeyValues;
  // any additional content (key value pairs) that should be logged with the event.
  contentTags: CustomKeyValues;
  // Name of the service
  serviceName: string;
};

//TODO: remove when upgrade to jquery types 3.X
type JQueryEventObjectWithAtt = JQueryEventObject & { originalEvent: EventWithAtt };
type EventWithAtt = Event & { srcElement: EventTargetWithAtt };
type EventTargetWithAtt = EventTarget & { hasAttribute?: (key: string) => boolean };

// @ngInject
export let pageActionTriggerDirective = function (
  loggingService: LoggingService,
  telemetryService: TelemetryService,
  $timeout: ng.ITimeoutService,
  telemetryConstants: GlobalTelemetryConstants,
  uiControlConstants: UiControlConstants
) {
  return {
    restrict: "A",
    link: function (scope: ng.IScope, element: ng.IAugmentedJQuery, attr: ng.IAttributes) {
      let options = <PageActionTriggerOptions>scope.$eval(attr["sdPageActionTrigger"]);
      let previousModelValue: any;
      let elementLogText: string;
      let actionType: string;

      let getMetaTags = () => {
        let metaTags = <CustomKeyValues>{};
        if (options.scenario) {
          metaTags[telemetryConstants.pageMetadataTags.scenario] = options.scenario;
          if (options.scenarioStep) {
            metaTags[telemetryConstants.pageMetadataTags.scenarioStep] = options.scenarioStep;
          }
        }
        if (options.pageType) {
          metaTags[telemetryConstants.pageMetadataTags.type] = options.pageType;
        }
        return metaTags;
      };

      let resolveBehavior = () => {
        if (options.behavior) {
          let behaviorString = scope.$eval(options.behavior);
          if (!behaviorString) {
            return undefined;
          } else if (!(behaviorString in telemetryConstants.pageActionBehavior)) {
            elementLogText = getElementLogText(element);
            loggingService.logWarning(
              `An invalid sdPageActionTrigger was configured -- a valid behavior (you provided '${behaviorString}') must be provided. Element: ${elementLogText}`
            );
          } else {
            return <any>telemetryConstants.pageActionBehavior[behaviorString];
          }
        }

        return telemetryConstants.pageActionBehavior.Unknown;
      };

      let pageActionInvoker = (element: ng.IAugmentedJQuery, pageActionBehaviorLocal: any) => {
        if (element.attr("data-bi-id")) {
          telemetryService.pushPageActionElementEvent(
            element,
            pageActionBehaviorLocal,
            scope.$eval(<any>options.contentTags),
            actionType || telemetryConstants.pageActionType.click,
            scope.$eval(<any>options.pageTags),
            getMetaTags(),
            options.serviceName
          );
        } else {
          telemetryService.pushPageActionEvent(
            actionType || telemetryConstants.pageActionType.click,
            pageActionBehaviorLocal,
            getMetaTags(),
            scope.$eval(<any>options.contentTags),
            scope.$eval(<any>options.pageTags),
            options.serviceName
          );
        }
      };

      let logIfModelAbsent = (model: any) => {
        if (!model) {
          elementLogText = getElementLogText(element);
          loggingService.logWarning(`Missing model to watch for element: ${elementLogText}`);
        }
      };

      let getElementLogText = (targetElement: ng.IAugmentedJQuery) => {
        return targetElement.clone().children().remove().end()[0].outerHTML;
      };

      let processPageAction = (element: ng.IAugmentedJQuery) => {
        if (options.model) {
          $timeout(() => {
            let model = scope.$eval(options.model);
            if (model !== previousModelValue) {
              pageActionInvoker(element, resolveBehavior());
              previousModelValue = model;
            }
          });
        }
      };

      let sourceElementDisabled = (event: JQueryEventObjectWithAtt) => {
        return (
          event.originalEvent &&
          event.originalEvent.srcElement &&
          event.originalEvent.srcElement.hasAttribute("disabled")
        );
      };

      let setActionType = (event: Event) => {
        if (event.type == "click") {
          actionType = telemetryConstants.pageActionType.click;
        } else {
          actionType = telemetryConstants.pageActionType.other;
        }
      };

      if (options.events) {
        if (options.events === "blur") {
          logIfModelAbsent(options.model);
        }

        element.on(options.events, (event) => {
          if (sourceElementDisabled(event)) {
            return;
          }
          setActionType(event);
          options.events === "blur" ? processPageAction(element) : pageActionInvoker(element, resolveBehavior());
        });
      }

      if (options.angularEvents) {
        logIfModelAbsent(options.model);

        angular.forEach(options.angularEvents, (event: any) => {
          scope.$on(event, () => {
            setActionType(event);
            processPageAction(element);
          });
        });
      }

      if (options.keypresses) {
        let keyStrings = options.keypresses.split(" ");
        let keyNumbers = <{ [key: string]: string }>{};
        keyStrings.forEach((keyString) => {
          switch (keyString.toLowerCase()) {
            // elevate this as a service if more things need functionality similar to it
            case "enter":
              keyNumbers[uiControlConstants.keyCode.enterKeyCode.toString()] =
                telemetryConstants.pageActionType.keyboardEnter;
              break;
            case "space":
              keyNumbers[uiControlConstants.keyCode.spaceKeyCode.toString()] =
                telemetryConstants.pageActionType.keyboardSpace;
              break;
            case "tab":
              keyNumbers[uiControlConstants.keyCode.spaceKeyCode.toString()] = telemetryConstants.pageActionType.other;
              break;
            default:
              loggingService.logError(
                "The key " + keyString + " is not registered. Events will not be captured for it."
              );
          }
        });

        element.on("keydown", (event: JQueryEventObject) => {
          if (sourceElementDisabled(event)) {
            return;
          }

          if (angular.isDefined(keyNumbers[event.keyCode.toString()])) {
            actionType = keyNumbers[event.keyCode.toString()];
            pageActionInvoker(element, resolveBehavior());
          }
        });
      }
    }
  };
};
